/* eslint-disable */
import { useState, useCallback } from 'react';
import { blockchain } from 'src/api/blockchain';
import { useBatchAttributions } from 'src/hooks/attributions/useBatchAttributions';
import { BitcoinTransactionItem } from 'src/interfaces';

const NAVBAR_HEIGHT_PLUS_TABLE_HEADER_HEIGHT = 220;

export interface UsePaginatedTxItemsProps {
  initialItems: BitcoinTransactionItem[];
  txHash: string;
  type: 'inputs' | 'outputs';
  totalCount: number;
  scrollTargetRef?: React.RefObject<HTMLElement>;
}

export interface UsePaginatedTxItemsReturn {
  items: BitcoinTransactionItem[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: (loadAll?: boolean) => Promise<void>;
  showLess: () => void;
  canShowLess: boolean;
}

export const usePaginatedTxItems = ({
  initialItems,
  txHash,
  type,
  totalCount,
  scrollTargetRef,
}: UsePaginatedTxItemsProps): UsePaginatedTxItemsReturn => {
  const [items, setItems] = useState<BitcoinTransactionItem[]>(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(initialItems.length);
  const hasMore = currentOffset < totalCount;
  const canShowLess = items.length > 5;

  const batchFetch = useBatchAttributions('bitcoin');

  const scrollToTarget = useCallback(() => {
    if (scrollTargetRef?.current) {
      const targetElement = scrollTargetRef.current;
      const targetPosition =
        targetElement.getBoundingClientRect().top +
        window.pageYOffset -
        NAVBAR_HEIGHT_PLUS_TABLE_HEADER_HEIGHT;

      window.scrollTo({
        top: targetPosition,
        behavior: 'auto',
      });
    }
  }, [scrollTargetRef]);

  const showLess = useCallback(() => {
    setItems(items.slice(0, 5));
    setCurrentOffset(5);

    scrollToTarget();
  }, [items, scrollToTarget]);

  const loadMore = async (loadAll: boolean = false) => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const limit = loadAll ? totalCount - currentOffset : 25;
      const response = await blockchain.getBitcoinTransactionInputsOutputs(
        txHash,
        type,
        currentOffset,
        limit,
      );

      const newItems = response.items;
      await batchFetch(newItems.map((item) => item.addr));

      setItems((prev) => [...prev, ...newItems]);
      setCurrentOffset((prev) => prev + newItems.length);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    items,
    isLoading,
    hasMore,
    loadMore,
    showLess,
    canShowLess,
  };
};
